import { useEffect, useState, type ReactNode } from 'react';
import { TextInput } from './ui.tsx';

/**
 * A number field that can actually be emptied.
 *
 * A control whose `value` is `String(someNumber)` cannot be cleared: deleting
 * the last digit reports "" to the parent, the parent stores 0 (or whatever
 * fallback its handler applies), and the very next render writes that digit
 * straight back into the box. The field then permanently reads "0", and
 * entering 15 means selecting the zero first — on a phone, precisely the
 * fiddliest thing to do.
 *
 * Two things fix that, and this component does both:
 *
 *  - What was typed lives in local state, and the parent's number is only
 *    re-read while the field is *not* being edited. A half-finished entry —
 *    empty, "0.", "1" on its way to "15" — survives the keystroke that made it.
 *  - `blankValue` names the number an empty box stands for. Where zero means
 *    "none" (no discount, no tax), the box shows nothing and the placeholder
 *    says what nothing means, instead of a 0 the user has to delete first.
 *
 * Range is left to the caller: `min`/`max` are passed to the input as the
 * browser's own hints, but clamping belongs with whoever owns the value, since
 * only they know whether an out-of-range entry should be pulled into range or
 * reported as an error.
 */

/** The text an entry field should show for a stored number. */
export function numberToInput(value: number | null | undefined, blankValue?: number): string {
  if (value === null || value === undefined) return '';
  if (blankValue !== undefined && value === blankValue) return '';
  return String(value);
}

/**
 * Reads what was typed back into a number. Empty — or anything a number field
 * cannot make sense of — is `blankValue` when the caller named one, and
 * otherwise `null`, i.e. "not answered" rather than zero.
 */
export function inputToNumber(text: string, blankValue?: number): number | null {
  if (text.trim() === '') return blankValue ?? null;
  const parsed = Number(text);
  if (!Number.isFinite(parsed)) return blankValue ?? null;
  return parsed;
}

interface NumberInputProps {
  label: string;
  hint?: ReactNode;
  error?: string;
  /** `null` renders an empty box; so does `blankValue`, when one is given. */
  value: number | null;
  onValueChange: (value: number | null) => void;
  /** The number an empty box stands for. Doubles as the default placeholder. */
  blankValue?: number;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

export function NumberInput({
  label,
  hint,
  error,
  value,
  onValueChange,
  blankValue,
  placeholder,
  min,
  max,
  step,
  disabled,
  required,
  className,
}: NumberInputProps) {
  const [text, setText] = useState(() => numberToInput(value, blankValue));
  const [editing, setEditing] = useState(false);

  // Adopt a value that changed elsewhere — a reset, a recalculation, a record
  // arriving from the server — but never mid-edit: while the field has focus,
  // an empty or half-typed box is the user's, and overwriting it then is the
  // exact behavior this component exists to avoid. Blur clears `editing`,
  // which re-runs this and is what tidies "007" back to "7" (and an entry the
  // caller pulled into range back to the number actually stored).
  useEffect(() => {
    if (editing) return;
    setText(numberToInput(value, blankValue));
  }, [value, blankValue, editing]);

  return (
    <TextInput
      label={label}
      hint={hint}
      error={error}
      type="number"
      autoComplete="off"
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      required={required}
      className={className}
      placeholder={placeholder ?? (blankValue === undefined ? undefined : String(blankValue))}
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        onValueChange(inputToNumber(event.target.value, blankValue));
      }}
      onFocus={() => setEditing(true)}
      onBlur={() => setEditing(false)}
    />
  );
}
